import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { io } from 'socket.io-client'
import { toast } from 'react-hot-toast'
import { useAuth } from './AuthContext'

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3001'

const SocketContext = createContext({})

export const useSocket = () => useContext(SocketContext)

export const SocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false)
  const [agents, setAgents] = useState([])
  const [messages, setMessages] = useState([])
  const [tasks, setTasks] = useState([])
  const socketRef = useRef(null)
  const { isAuthenticated, user } = useAuth()

  useEffect(() => {
    if (!isAuthenticated || !user) return

    // Initialize socket connection
    const socket = io(SOCKET_URL, {
      auth: {
        token: localStorage.getItem('token')
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    })

    socketRef.current = socket

    // Connection events
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id)
      setIsConnected(true)
      toast.success('Connected to real-time server')
    })

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason)
      setIsConnected(false)
      if (reason === 'io server disconnect') {
        toast.error('Disconnected from server. Reconnecting...')
      }
    })

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error)
      toast.error('Connection error. Retrying...')
    })

    // Agent events
    socket.on('agent:status', (agentData) => {
      setAgents(prev => {
        const index = prev.findIndex(a => a.agent_id === agentData.agent_id)
        if (index >= 0) {
          const updated = [...prev]
          updated[index] = { ...updated[index], ...agentData }
          return updated
        } else {
          return [...prev, agentData]
        }
      })
    })

    socket.on('agent:list', (agentList) => {
      setAgents(agentList)
    })

    // Message events
    socket.on('message:new', (message) => {
      setMessages(prev => [...prev, message])
      // Show notification for new messages not from current user
      if (message.sender_id !== user?.id) {
        toast(`New message from ${message.sender_name}`, {
          icon: '💬',
          duration: 3000
        })
      }
    })

    socket.on('message:history', (messageHistory) => {
      setMessages(messageHistory)
    })

    // Task events
    socket.on('task:new', (task) => {
      setTasks(prev => [...prev, task])
      toast.success(`New task: ${task.title}`)
    })

    socket.on('task:update', (updatedTask) => {
      setTasks(prev => 
        prev.map(task => 
          task.id === updatedTask.id ? updatedTask : task
        )
      )
    })

    socket.on('task:list', (taskList) => {
      setTasks(taskList)
    })

    // System events
    socket.on('system:alert', (alert) => {
      toast(alert.message, {
        icon: alert.type === 'error' ? '❌' : '⚠️',
        duration: 5000
      })
    })

    // Request initial data
    socket.emit('agent:getAll')
    socket.emit('task:getAll')
    socket.emit('message:getRecent')

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [isAuthenticated, user])

  // Socket methods
  const sendMessage = (receiverId, content, messageType = 'text') => {
    if (!socketRef.current || !isConnected) {
      toast.error('Not connected to server')
      return false
    }

    socketRef.current.emit('message:send', {
      receiver_id: receiverId,
      content,
      message_type: messageType
    })
    return true
  }

  const createTask = (taskData) => {
    if (!socketRef.current || !isConnected) {
      toast.error('Not connected to server')
      return false
    }

    socketRef.current.emit('task:create', taskData)
    return true
  }

  const updateTask = (taskId, updates) => {
    if (!socketRef.current || !isConnected) {
      toast.error('Not connected to server')
      return false
    }

    socketRef.current.emit('task:update', { taskId, ...updates })
    return true
  }

  const updateAgentStatus = (status, currentTask = null, progress = 0) => {
    if (!socketRef.current || !isConnected) {
      return false
    }

    socketRef.current.emit('agent:updateStatus', {
      status,
      current_task: currentTask,
      progress
    })
    return true
  }

  const value = {
    socket: socketRef.current,
    isConnected,
    agents,
    messages,
    tasks,
    sendMessage,
    createTask,
    updateTask,
    updateAgentStatus
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}